import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Image,
  Input,
  Link,
  Radio,
  RadioGroup,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import SignatureCanvas from 'react-signature-canvas';
import { SiGooglepay, SiPaypal } from 'react-icons/si';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import satispayLogo from '../images/satispay.png';
import PaymentForm from './PaymentForm';
import TermsConditions from './TermsConditions';
import {
  getAgeFromBirthDate,
  getBirthDateFromCodiceFiscale,
  normalizeCodiceFiscale,
} from '../utils/codiceFiscale';
import { isEventEffectivelyFree } from '../utils';
import { isOrderAlreadyCreated } from '../utils/orderCreation';
import { getFreeEventOptionsForDay } from '../utils/eventiDiOggi';
import { trackEvent } from '../utils/umami';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const TESSERA_PRICE = 10;

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[0-9+\-()\s]{7,20}$/;

const initialFormData = {
  email: '',
  nomeSocio: '',
  cognomeSocio: '',
  codiceFiscaleSocio: '',
  luogoNascitaSocio: '',
  telefono: '',
  autorizzazioneFotografie: true,
  consensoPubblicazione: true,
  sendNewsletter: true,
  terminiCondizioni: false,
  paymentMode: 'online', // 'online' | 'sede'
};

// Stato del codice fiscale: data di nascita ricavata ed eventuale errore
const getCodiceFiscaleInfo = (value) => {
  const cf = normalizeCodiceFiscale(value);
  if (cf.length < 16) return { birthDate: null, error: null, isMinor: false };
  const birthDate = getBirthDateFromCodiceFiscale(cf);
  if (!birthDate) {
    return {
      birthDate: null,
      error: 'Codice fiscale non valido',
      isMinor: false,
    };
  }
  return {
    birthDate,
    error: null,
    isMinor: getAgeFromBirthDate(birthDate) < 18,
  };
};

// Metodi offerti dal pagamento online (Stripe Payment Element)
const PaymentLogos = () => (
  <HStack
    as="span"
    display="inline-flex"
    spacing={2}
    ml={2}
    verticalAlign="middle"
  >
    <Box as={SiPaypal} color="#003087" boxSize="16px" aria-label="PayPal" />
    <Image
      src={satispayLogo}
      alt="Satispay"
      boxSize="16px"
      borderRadius="4px"
    />
    <Box
      as={SiGooglepay}
      color="gray.600"
      boxSize="28px"
      aria-label="Google Pay"
    />
  </HStack>
);

const formatDate = (isoDate) => isoDate.split('-').reverse().join('/');

// Evento gratuito senza varianti: l'iscrizione si crea insieme alla tessera
export const canBookWithTessera = (event) =>
  Boolean(event) && !event.Varianti?.length && isEventEffectivelyFree(event);

/**
 * Tesseramento rapido per maggiorenni: solo i dati essenziali, firma e
 * pagamento online (Stripe) oppure in sede. L'ordine viene creato dal webhook
 * Stripe, quindi non serve che l'utente sia autenticato.
 *
 * Senza `event` (pagina /tesseramento-rapido) si possono scegliere gli eventi
 * gratuiti di oggi: il nuovo account viene autenticato e iscritto subito.
 *
 * Modalità checkout evento: `autoLogin` fa autenticare il nuovo account e
 * `onContinue` viene chiamato per passare all'iscrizione all'evento. Se
 * `event` è gratuito e senza varianti il nuovo socio viene iscritto subito e
 * `onDone` chiude il checkout.
 */
const TesseramentoRapido = ({
  luogoFirma = 'Online',
  onSuccess,
  autoLogin = false,
  onContinue,
  onDone,
  event,
  embedded = false,
}) => {
  const location = useLocation();
  const sigCanvas = useRef(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'payment' | 'done'
  const [clientSecret, setClientSecret] = useState('');
  const [paid, setPaid] = useState(false);
  const [booked, setBooked] = useState(false);
  const [todayOptions, setTodayOptions] = useState([]);
  const [selectedToday, setSelectedToday] = useState([]);
  const [todayResults, setTodayResults] = useState({ booked: [], failed: [] });

  const bookEvent = canBookWithTessera(event);

  // Eventi del giorno solo sulla pagina autonoma, non nel checkout di un evento
  useEffect(() => {
    if (event) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get('/api/eventi');
        if (!cancelled) {
          setTodayOptions(getFreeEventOptionsForDay(response?.data?.events));
        }
      } catch (err) {
        if (!cancelled) setTodayOptions([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [event]);

  const toggleToday = (key) =>
    setSelectedToday((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const cardBg = useColorModeValue('white', 'gray.800');
  const canvasBg = useColorModeValue('white', '#2D3748');
  const penColor = useColorModeValue('black', 'white');

  const cfInfo = getCodiceFiscaleInfo(formData.codiceFiscaleSocio);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email obbligatoria';
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = 'Formato email non valido (es. nome@dominio.com)';
    }
    if (!formData.nomeSocio.trim()) newErrors.nomeSocio = 'Campo obbligatorio';
    if (!formData.cognomeSocio.trim()) {
      newErrors.cognomeSocio = 'Campo obbligatorio';
    }
    if (!formData.luogoNascitaSocio.trim()) {
      newErrors.luogoNascitaSocio = 'Campo obbligatorio';
    }
    if (!formData.codiceFiscaleSocio) {
      newErrors.codiceFiscaleSocio = 'Campo obbligatorio';
    } else if (!cfInfo.birthDate) {
      newErrors.codiceFiscaleSocio = 'Codice fiscale non valido';
    } else if (cfInfo.isMinor) {
      newErrors.codiceFiscaleSocio =
        'Il tesseramento rapido è riservato ai maggiorenni';
    }
    if (formData.telefono && !PHONE_REGEX.test(formData.telefono)) {
      newErrors.telefono = 'Telefono non valido';
    }
    if (!formData.terminiCondizioni) {
      newErrors.terminiCondizioni = 'Devi accettare i termini e condizioni';
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      newErrors.firma = 'La firma è obbligatoria';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // /api/cmil risponde sempre 401 con { exists }
  const isEmailRegistered = async (email) => {
    try {
      await axios.post('/api/cmil', { e: email });
    } catch (err) {
      return Boolean(err.response?.data?.exists);
    }
    return false;
  };

  const createPaymentIntent = async (socio) => {
    const response = await axios.post('/api/create-payment-intent', {
      amount: TESSERA_PRICE * 100,
      currency: 'eur',
      socioId: socio.documentId,
      // Il webhook invia la conferma via email (l'utente non è loggato)
      source: 'tesseramento-rapido',
      eventVariants: {
        [socio.documentId]: {
          name: `Tesseramento ${socio.nomeSocio}`,
          type: 'tesseramento',
          priceOption: 'totale',
          amount: TESSERA_PRICE,
        },
      },
    });
    return response.data.clientSecret;
  };

  // Iscrizione gratuita del nuovo socio a un evento (o a sue varianti)
  const createFreeBooking = async (socio, targetEvent, variants = []) => {
    try {
      const response = await axios.post('/api/ordini', {
        eventId: targetEvent.documentId,
        participantId: socio.documentId,
        paymentStatus: 'succeeded',
        paymentMethod: 'contanti',
        amount: 0,
        deckDocumentId: null,
        eventVariants:
          variants.length > 0
            ? Object.fromEntries(
                variants.map((variant) => [
                  variant.documentId,
                  {
                    type: 'variant',
                    priceOption: 'totale',
                    name: `${targetEvent.Nome} - ${variant.Nome}`,
                  },
                ])
              )
            : {
                [targetEvent.documentId]: {
                  type: 'event',
                  priceOption: 'totale',
                  name: targetEvent.Nome,
                },
              },
      });
      return (
        Boolean(response.data?.order) || isOrderAlreadyCreated(response.data)
      );
    } catch (err) {
      return false;
    }
  };

  // Se fallisce resta il flusso "Continua con l'iscrizione all'evento"
  const bookFreeEvent = async (socio) => {
    setBooked(await createFreeBooking(socio, event));
  };

  // Un ordine per evento, con tutte le sue varianti scelte
  const bookTodayEvents = async (socio, chosen) => {
    const byEvent = new Map();
    chosen.forEach((option) => {
      const entry = byEvent.get(option.event.documentId) || {
        event: option.event,
        variants: [],
        labels: [],
      };
      if (option.variant) entry.variants.push(option.variant);
      entry.labels.push(option.label);
      byEvent.set(option.event.documentId, entry);
    });
    const results = { booked: [], failed: [] };
    // eslint-disable-next-line no-restricted-syntax
    for (const entry of byEvent.values()) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await createFreeBooking(socio, entry.event, entry.variants);
      results[ok ? 'booked' : 'failed'].push(...entry.labels);
    }
    setTodayResults(results);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    trackEvent('membership_start', { flow: 'tesseramento_rapido' });
    setLoading(true);
    try {
      const email = formData.email.trim();
      if (await isEmailRegistered(email)) {
        setErrors((prev) => ({
          ...prev,
          email: 'Email già registrata: accedi per tesserarti dal tuo account',
        }));
        return;
      }

      const { paymentMode, ...socioFields } = formData;
      const chosenToday = todayOptions.filter((option) =>
        selectedToday.includes(option.key)
      );
      // /api/ordini richiede la sessione: con eventi scelti serve il login
      const needsLogin = autoLogin || chosenToday.length > 0;
      const response = await axios.post('/api/tesseramento', {
        ...socioFields,
        fonte: 'rapido',
        email,
        user: email,
        isMinorenne: false,
        codiceFiscaleSocio: normalizeCodiceFiscale(formData.codiceFiscaleSocio),
        dataNascitaSocio: cfInfo.birthDate,
        data: new Date().toISOString().split('T')[0],
        luogo: luogoFirma,
        firma: sigCanvas.current.getTrimmedCanvas().toDataURL('image/png'),
        paymentStatus: 'pending',
        ...(needsLogin ? { autoLogin: true } : {}),
      });
      const { socio } = response.data;
      setLoggedIn(Boolean(response.data.loggedIn));
      onSuccess?.(socio);
      // /api/ordini richiede la sessione del nuovo account
      if (bookEvent && response.data.loggedIn) await bookFreeEvent(socio);
      if (chosenToday.length > 0) {
        if (response.data.loggedIn) {
          await bookTodayEvents(socio, chosenToday);
        } else {
          setTodayResults({
            booked: [],
            failed: chosenToday.map((option) => option.label),
          });
        }
      }

      if (paymentMode === 'online') {
        try {
          setClientSecret(await createPaymentIntent(socio));
          setPhase('payment');
        } catch (piError) {
          // Il socio esiste già: il pagamento si può completare dall'account
          setSubmitError(
            'Tesseramento registrato, ma non è stato possibile avviare il pagamento online. Potrai pagare dalla tua area personale, in sede o in location.'
          );
          setPhase('done');
        }
      } else {
        setPhase('done');
      }
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
          'Errore durante il tesseramento. Riprova o contattaci.'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderDone = () => (
    <VStack spacing={4} align="stretch">
      <Alert status="success" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          <b>Benvenuto in CIL!</b>{' '}
          {paid
            ? 'Pagamento ricevuto, la tua tessera è attiva.'
            : 'Il tesseramento è registrato.'}
        </AlertDescription>
      </Alert>
      {submitError && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {submitError}
        </Alert>
      )}
      {booked && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Sei iscritto a <b>{event.Nome}</b>.
          </AlertDescription>
        </Alert>
      )}
      <Text>
        Ti abbiamo inviato un&apos;email con la password temporanea per accedere
        all&apos;area riservata.
        {!paid &&
          ` La quota di €${TESSERA_PRICE} si può pagare in sede, in location oppure online dal tuo account.`}
      </Text>
      <Text fontSize="sm" color="gray.500">
        Dal tuo account puoi completare il profilo (indirizzo, interessi,
        consensi).
      </Text>
      {todayResults.booked.length > 0 && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Sei iscritto a <b>{todayResults.booked.join(', ')}</b>.
          </AlertDescription>
        </Alert>
      )}
      {todayResults.failed.length > 0 && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Non è stato possibile iscriverti a{' '}
            <b>{todayResults.failed.join(', ')}</b>: chiedi allo staff
            all&apos;ingresso.
          </AlertDescription>
        </Alert>
      )}
      {booked && onDone && (
        <Button colorScheme="blue" size="lg" onClick={onDone}>
          Fatto
        </Button>
      )}
      {onContinue && loggedIn && !booked && (
        <Button colorScheme="blue" size="lg" onClick={onContinue}>
          Continua con l&apos;iscrizione all&apos;evento
        </Button>
      )}
      {/* Senza login automatico si accede con la password ricevuta e si torna qui */}
      {onContinue && !loggedIn && (
        <Button
          as={RouterLink}
          to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
          colorScheme="blue"
          size="lg"
        >
          Accedi e torna all&apos;evento
        </Button>
      )}
      {/* Dopo il login automatico serve ricaricare l'app per leggere la sessione */}
      {!onContinue && loggedIn && (
        <Button as="a" href="/account" colorScheme="blue">
          Vai al tuo account
        </Button>
      )}
      {!onContinue && !loggedIn && (
        <Button as={RouterLink} to="/login" colorScheme="blue">
          Accedi
        </Button>
      )}
    </VStack>
  );

  const renderPayment = () => (
    <VStack spacing={4} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        Dati registrati. Completa il pagamento per attivare la tessera.
      </Alert>
      <Text fontSize="2xl" textAlign="right">
        Totale: <b>€{TESSERA_PRICE.toFixed(2)}</b>
      </Text>
      {clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm
            onSuccess={({ status }) => {
              if (['succeeded', 'processing'].includes(status)) {
                setPaid(true);
                setPhase('done');
              }
            }}
          />
        </Elements>
      ) : (
        <Center py={4}>
          <Spinner size="lg" />
        </Center>
      )}
      <Button variant="link" size="sm" onClick={() => setPhase('done')}>
        Preferisco pagare in sede / in location
      </Button>
    </VStack>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} noValidate>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired isInvalid={Boolean(errors.email)}>
          <FormLabel>Email</FormLabel>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />
          <FormErrorMessage>
            {errors.email}
            {errors.email?.startsWith('Email già registrata') && (
              <Link as={RouterLink} to="/login" ml={1} fontWeight="bold">
                Accedi
              </Link>
            )}
          </FormErrorMessage>
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired isInvalid={Boolean(errors.nomeSocio)}>
            <FormLabel>Nome</FormLabel>
            <Input
              name="nomeSocio"
              autoComplete="given-name"
              value={formData.nomeSocio}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.nomeSocio}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={Boolean(errors.cognomeSocio)}>
            <FormLabel>Cognome</FormLabel>
            <Input
              name="cognomeSocio"
              autoComplete="family-name"
              value={formData.cognomeSocio}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.cognomeSocio}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <FormControl
          isRequired
          isInvalid={Boolean(errors.codiceFiscaleSocio || cfInfo.error)}
        >
          <FormLabel>Codice fiscale</FormLabel>
          <Input
            name="codiceFiscaleSocio"
            textTransform="uppercase"
            maxLength={16}
            autoComplete="off"
            value={formData.codiceFiscaleSocio}
            onChange={handleChange}
          />
          {cfInfo.birthDate &&
            !cfInfo.isMinor &&
            !errors.codiceFiscaleSocio && (
              <FormHelperText>
                Data di nascita: {formatDate(cfInfo.birthDate)}
              </FormHelperText>
            )}
          <FormErrorMessage>
            {errors.codiceFiscaleSocio || cfInfo.error}
          </FormErrorMessage>
        </FormControl>

        {cfInfo.isMinor && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Per i minorenni serve il tesseramento completo con i dati del
              genitore.{' '}
              <Link as={RouterLink} to="/tesseramento" fontWeight="bold">
                Vai al tesseramento completo
              </Link>
            </AlertDescription>
          </Alert>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isRequired isInvalid={Boolean(errors.luogoNascitaSocio)}>
            <FormLabel>Luogo di nascita</FormLabel>
            <Input
              name="luogoNascitaSocio"
              value={formData.luogoNascitaSocio}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.luogoNascitaSocio}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.telefono)}>
            <FormLabel>Telefono (facoltativo)</FormLabel>
            <Input
              name="telefono"
              type="tel"
              autoComplete="tel"
              value={formData.telefono}
              onChange={handleChange}
            />
            <FormErrorMessage>{errors.telefono}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <Divider />

        <FormControl>
          <FormLabel>Pagamento della quota (€{TESSERA_PRICE})</FormLabel>
          <RadioGroup
            value={formData.paymentMode}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, paymentMode: value }))
            }
          >
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <Radio value="online" whiteSpace="nowrap">
                Online con carta
                <PaymentLogos />
              </Radio>
              <Radio value="sede">In sede / in location</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {todayOptions.length > 0 && (
          <>
            <Divider />
            <Stack spacing={2}>
              <Text fontSize="sm" fontWeight="600">
                Eventi di oggi
              </Text>
              <Text fontSize="sm" color="gray.500">
                Scegli gli eventi gratuiti a cui vuoi partecipare: ti iscriviamo
                insieme alla tessera.
              </Text>
              {todayOptions.map((option) => (
                <Checkbox
                  key={option.key}
                  isChecked={selectedToday.includes(option.key)}
                  onChange={() => toggleToday(option.key)}
                >
                  <Text fontSize="sm">
                    {option.time && <b>{option.time} · </b>}
                    {option.label}
                  </Text>
                </Checkbox>
              ))}
            </Stack>
          </>
        )}

        {bookEvent && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Insieme alla tessera ti iscriviamo a <b>{event.Nome}</b>{' '}
              (gratuito).
            </AlertDescription>
          </Alert>
        )}

        <Divider />

        <Stack spacing={2}>
          <Text fontSize="sm" fontWeight="600">
            Consensi facoltativi
          </Text>
          <Checkbox
            name="autorizzazioneFotografie"
            isChecked={formData.autorizzazioneFotografie}
            onChange={handleChange}
          >
            <Text fontSize="sm">
              Autorizzo l&apos;uso delle mie fotografie per archivio e
              documentazione delle attività.
            </Text>
          </Checkbox>
          <Checkbox
            name="consensoPubblicazione"
            isChecked={formData.consensoPubblicazione}
            onChange={handleChange}
          >
            <Text fontSize="sm">
              Acconsento alla pubblicazione di foto, audio e video sul web e sui
              social.
            </Text>
          </Checkbox>
          <Checkbox
            name="sendNewsletter"
            isChecked={formData.sendNewsletter}
            onChange={handleChange}
          >
            <Text fontSize="sm">Voglio ricevere la newsletter.</Text>
          </Checkbox>
        </Stack>

        <FormControl isInvalid={Boolean(errors.terminiCondizioni)}>
          <Checkbox
            name="terminiCondizioni"
            isChecked={formData.terminiCondizioni}
            onChange={handleChange}
          >
            <Text fontSize="sm">
              Chiedo di aderire all&apos;associazione e accetto termini e
              condizioni *
            </Text>
          </Checkbox>
          <FormErrorMessage>{errors.terminiCondizioni}</FormErrorMessage>
          <TermsConditions />
        </FormControl>

        <FormControl isRequired isInvalid={Boolean(errors.firma)}>
          <FormLabel>Firma</FormLabel>
          <SignatureCanvas
            ref={sigCanvas}
            clearOnResize={false}
            penColor={penColor}
            onEnd={() => setErrors((prev) => ({ ...prev, firma: '' }))}
            canvasProps={{
              style: {
                width: '100%',
                height: '150px',
                border: '1px solid #dddddd',
                borderRadius: '8px',
                backgroundColor: canvasBg,
              },
            }}
          />
          <Button
            mt={2}
            size="sm"
            variant="ghost"
            onClick={() => sigCanvas.current?.clear()}
          >
            Cancella firma
          </Button>
          <FormErrorMessage>{errors.firma}</FormErrorMessage>
        </FormControl>

        {submitError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {submitError}
          </Alert>
        )}

        <Button
          type="submit"
          colorScheme="yellow"
          size="lg"
          isLoading={loading}
          loadingText="Invio in corso..."
          isDisabled={cfInfo.isMinor}
        >
          {formData.paymentMode === 'online'
            ? 'Continua al pagamento'
            : `Completa il tesseramento${
                bookEvent || selectedToday.length > 0 ? ' e iscriviti' : ''
              }`}
        </Button>
      </VStack>
    </form>
  );

  const content = (
    <>
      {phase === 'form' && renderForm()}
      {phase === 'payment' && renderPayment()}
      {phase === 'done' && renderDone()}
    </>
  );

  if (embedded) return content;

  return (
    <Box
      bg={cardBg}
      p={{ base: 4, md: 8 }}
      maxW="600px"
      w="100%"
      mx="auto"
      borderRadius="lg"
      boxShadow="lg"
    >
      <Heading size="lg" mb={1}>
        Tesseramento rapido
      </Heading>
      <Text color="gray.500" mb={6}>
        Per maggiorenni · pochi dati, un minuto.
      </Text>
      {content}
    </Box>
  );
};

export default TesseramentoRapido;
